import { useMemo } from "react";
import { MVTLayer } from "deck.gl";
import { Feature, Geometry } from "geojson";
import type { RgbColour } from "@/utils/colourUtils";
import type { TrajectoryPointDtoVehicleType } from "@/api/generated/models/trajectoryPointDtoVehicleType";
import { TileFeature, TileFeatureProperties } from "@/types/tileFeature";

// https://deck.gl/docs/api-reference/geo-layers/tile-layer#debouncetime
export const DebounceTimeMilliseconds = 300;

// https://deck.gl/docs/api-reference/geo-layers/tile-layer#maxrequests
export const MaxConcurrentRequests = 10;
export const BackgroundTileZoom = 14;
export function useTrajectoriesLayer(tileUrl: string, colourAccessor: (f: TileFeature) => RgbColour, displayedVehicleTypes: TrajectoryPointDtoVehicleType[], visible: boolean) {
  const vehicleTypeSet = useMemo(() => new Set(displayedVehicleTypes), [displayedVehicleTypes]);
  const getLineColor = (f: Feature<Geometry, TileFeatureProperties>) => colourAccessor(f);
  const getFillColor = (f_0: Feature<Geometry, TileFeatureProperties>) => colourAccessor(f_0);
  const getLineWidth = (f_1: Feature<Geometry, TileFeatureProperties>) => {
    const vehicleType = f_1.properties.vehicleType;
    return vehicleType && !vehicleTypeSet.has(vehicleType) ? 0 : 0.2;
  };
  const updateTriggers = {
    getLineColor: [colourAccessor],
    getFillColor: [colourAccessor],
    getLineWidth: [vehicleTypeSet]
  };
  const backgroundLayer = useMemo(() => new MVTLayer({
    id: "vehicle-trajectories-background",
    visible,
    data: visible ? tileUrl : null,
    debounceTime: DebounceTimeMilliseconds,
    maxRequests: MaxConcurrentRequests,
    minZoom: BackgroundTileZoom,
    maxZoom: BackgroundTileZoom,
    getLineColor,
    getFillColor,
    getLineWidth,
    updateTriggers
  }), [tileUrl, colourAccessor, vehicleTypeSet, visible]);
  const foregroundLayer = useMemo(() => new MVTLayer({
    id: "vehicle-trajectories",
    visible,
    data: visible ? tileUrl : null,
    debounceTime: DebounceTimeMilliseconds,
    maxRequests: MaxConcurrentRequests,
    minZoom: 14,
    maxZoom: 17,
    getLineColor,
    getFillColor,
    getLineWidth,
    updateTriggers
  }), [tileUrl, colourAccessor, vehicleTypeSet, visible]);
  return useMemo(() => [backgroundLayer, foregroundLayer] as const, [backgroundLayer, foregroundLayer]);
}